import { ButtonV2, Checkbox } from '@cimplico/components';
import { Link } from 'react-router-dom';
import { useWorkpaperContext } from '../../providers/WorkpaperProvider';
import { TableCellsIcon } from '@heroicons/react/24/outline';
import { useJournals } from '../../queries/use-journals';
import { JournalOverview } from '../../types/journal';
import { useState } from 'react';
import { useIsWorkpaperRole } from '../../hooks/use-is-workpaper-role';
import { WorkpaperRoleEnum } from '../../types/workpaper';
import { isNullOrUndefined } from '../new-workpaper/utils';
import { useDeleteJournals } from '../../queries/use-delete-journals';
import { toastNotification } from '../../components/toast/Toast';
import EmptyStateNew from '../../components/empty-state/EmptyStateNew';
import plural from '../../utilities/plural';
import PostJournalsConfirmDialog from '../journals/PostJournalsConfirmDialog';
import DeleteJournalsConfirmDialog from '../journal-overview/DeleteJournalsConfirmDialog';
import PostJournalsErrorAlert from '../../components/post-journals-error-alert/PostJournalsErrorAlert';
import SummaryTableTitle from './SummaryTableTitle';
import JournalDetails from '../journal-overview/JournalDetails';
import Currency from '../../components/currency/Currency';
import queryClient from '../../query-client';

type DraftJournalsProps = {
    onXeroError: (error?: { name: string; message: string }) => void;
};

function DraftJournals({ onXeroError }: DraftJournalsProps): JSX.Element {
    const { id, readOnly, role, permissions } = useWorkpaperContext();
    const { isGuest } = useIsWorkpaperRole();

    const [selectedJournals, setSelectedJournals] = useState<JournalOverview[]>([]);
    const [action, setAction] = useState<string | undefined>(undefined);
    const [errorMessages, setErrorMessages] = useState<{ [key: number]: string[] } | undefined>(undefined);

    const journalsQuery = useJournals(id, {
        filter: 'status=DRAFT',
        search: '',
        sort: 'sort[dir]=asc&sort[field]=createdAt',
    });

    const deleteJournalsQuery = useDeleteJournals();

    const roleCanPost =
        role === WorkpaperRoleEnum.REVIEWER ||
        role === WorkpaperRoleEnum.APPROVER ||
        (WorkpaperRoleEnum.PREPARER && permissions.preparer.canPostJournal);

    const canPostJournals = selectedJournals.filter(
        (j) => !isNullOrUndefined(j.debitTotal) && !isNullOrUndefined(j.creditTotal) && j.debitTotal === j.creditTotal
    );

    function resetAction(): void {
        setAction(undefined);
        setSelectedJournals([]);
        queryClient.invalidateQueries(['workpapers', id]);
        queryClient.invalidateQueries(['workpaper-progress', id]);
    }

    function handleJournalSelect(journal: JournalOverview): void {
        setSelectedJournals((prev) => {
            if (prev.find((p) => p.id === journal.id)) {
                return prev.filter((p) => p.id !== journal.id);
            }
            return prev.concat([journal]);
        });
    }

    function deleteJournals(): void {
        deleteJournalsQuery.mutate(
            { ids: selectedJournals.map((journal) => journal.id) },
            {
                onSuccess: () => {
                    resetAction();
                    toastNotification(
                        'success',
                        `${selectedJournals.length} draft ${plural(selectedJournals.length, 'journal', 'journals')} deleted!`
                    );
                },
            }
        );
    }

    return (
        <>
            <PostJournalsConfirmDialog
                journalIds={canPostJournals.map((j) => j.id)}
                isOpen={action === 'POST'}
                onClose={(): void => setAction(undefined)}
                onPost={resetAction}
                onXeroError={onXeroError}
                onPostError={setErrorMessages}
            />
            {action === 'DELETE' && (
                <DeleteJournalsConfirmDialog
                    journalIds={selectedJournals.map((j) => j.id)}
                    onClose={(): void => setAction(undefined)}
                    onDelete={deleteJournals}
                />
            )}
            <div>
                <div className="border-b pb-3 flex justify-between items-center">
                    <SummaryTableTitle
                        title="Journals"
                        count={journalsQuery.data?.journals ? (journalsQuery.data.journals.length as number).toString() : '0'}
                        status="Draft"
                        statusBadgeColor="gray"
                    />
                    {selectedJournals.length > 0 ? (
                        <div className="-mt-2 flex gap-2">
                            {roleCanPost && (
                                <ButtonV2
                                    label={`Post ${plural(canPostJournals.length, 'Journal', 'Journals')} (${canPostJournals.length})`}
                                    size="xs"
                                    onClick={(): void => setAction('POST')}
                                    isDisabled={readOnly || canPostJournals.length === 0}
                                    type="secondary"
                                />
                            )}
                            <ButtonV2
                                label={`Delete ${plural(selectedJournals.length, 'Journal', 'Journals')} (${selectedJournals.length})`}
                                size="xs"
                                onClick={(): void => (selectedJournals.length > 1 ? setAction('DELETE') : deleteJournals())}
                                isDisabled={readOnly || isGuest}
                                type="danger"
                            />
                        </div>
                    ) : (
                        <Link to="../journals?tab=draft" className="text-xs font-medium text-gray-700">
                            See all
                        </Link>
                    )}
                </div>
                {journalsQuery.isLoading && <></>}
                {journalsQuery.isError && <p className="block text-center text-sm text-gray-500 pt-4">Unable to get draft journals.</p>}
                {journalsQuery.isSuccess && journalsQuery.data.journals && (
                    <div className="max-h-[350px] overflow-y-auto">
                        {errorMessages && (
                            <div className="mt-2 -mb-2">
                                <PostJournalsErrorAlert errors={errorMessages} onClose={(): void => setErrorMessages(undefined)} />
                            </div>
                        )}
                        {journalsQuery.data.journals.length === 0 ? (
                            <div className="mt-12">
                                <EmptyStateNew
                                    icon={<TableCellsIcon />}
                                    title="No draft journals"
                                    titleSize="small"
                                    helpText="See any draft journals preventing certification."
                                    helpLink="https://learn.cimplico.com/docs/dashboard-overview#common-blockers"
                                />
                            </div>
                        ) : (
                            <div className="mt-2">
                                {journalsQuery.data.journals.map((j) => {
                                    return (
                                        <Journal
                                            key={j.id}
                                            journal={j}
                                            onSelect={handleJournalSelect}
                                            isChecked={!!selectedJournals.find((selected) => selected.id === j.id)}
                                        />
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </>
    );
}

type JournalProps = {
    journal: JournalOverview;
    isChecked: boolean;
    onSelect: (journal: JournalOverview) => void;
};

function Journal({ journal, isChecked, onSelect }: JournalProps): JSX.Element {
    return (
        <div className="flex items-center justify-between p-2 border-b">
            <div className="flex gap-4">
                <Checkbox checked={isChecked} onClick={(): void => onSelect(journal)} />
                <div className="flex flex-col items-start gap-1">
                    <Link to={`../journals/${journal.id}`}>
                        <p className="text-indigo-600 dark:text-indigo-400 text-sm cursor-pointer font-medium">
                            JNL#{journal.reference.toString().padStart(3, '0')}
                            {journal.narration ? ` - ${journal.narration}` : ''}
                        </p>
                    </Link>
                    <JournalDetails journal={journal} />
                </div>
            </div>
            <div className="grid grid-cols-2 gap-4 w-[30%] justify-items-end">
                <Currency value={journal.debitTotal} description="Dr" className="text-xs text-gray-700 dark:text-white/[0.87]" />
                <Currency value={journal.creditTotal} description="Cr" className="text-xs text-gray-700 dark:text-white/[0.87]" />
            </div>
        </div>
    );
}

export default DraftJournals;
